/**
 * Field rules mirrored from the server so the form can answer before a round trip.
 *
 * These are a convenience, never the enforcement. Every rule here is also declared as a
 * DataAnnotation on the request DTO, and the API rejects a bad value whatever the browser did —
 * a client-side check is a courtesy to the person typing, not a security boundary.
 */

/**
 * Phone numbers: 10-digit US numbers written (999) 999-9999. Mirrors `PhoneNumber.Pattern` in
 * IdVerify.Core character for character (area code and exchange never start with 0 or 1). Phone
 * inputs run `formatUsPhoneInput` on every change, so typing "3125551234" shows "(312) 555-1234".
 *
 * If you change one, change the other. The C# side is the authority — it is what actually
 * rejects the value.
 */
export const PHONE_PATTERN = /^\([2-9][0-9]{2}\) [2-9][0-9]{2}-[0-9]{4}$/;

/** Kept identical to `PhoneNumber.ErrorMessage` so both portals say the same thing. */
export const PHONE_ERROR = 'Enter a 10-digit US phone number in the format (999) 999-9999.';

/**
 * Digits only, for phone and one-time-code inputs: blocks any other printable key (onKeyDown)
 * and, for mobile keyboards that do not report keys, any other typed text (onBeforeInput).
 * Pasted text is cleaned by each field's onChange. Same behaviour as data-digits-only /
 * data-us-phone in web/wwwroot/js/site.js.
 */
export const digitsOnly = {
  onKeyDown(e: { key: string; ctrlKey: boolean; metaKey: boolean; altKey: boolean; preventDefault(): void }) {
    if (e.ctrlKey || e.metaKey || e.altKey) return;
    if (e.key.length === 1 && !/[0-9]/.test(e.key)) e.preventDefault();
  },
  onBeforeInput(e: { nativeEvent: Event; preventDefault(): void }) {
    const ev = e.nativeEvent as InputEvent;
    if (ev.inputType === 'insertText' && ev.data && /[^0-9]/.test(ev.data)) e.preventDefault();
  },
};

/**
 * Formats as the user types: keeps the digits (dropping a leading US "1" country code), at most
 * ten, and lays them out as (999) 999-9999 so far as they go. Same as formatUsPhoneInput in
 * web/wwwroot/js/site.js.
 */
export function formatUsPhoneInput(value: string): string {
  let d = (value || '').replace(/\D/g, '');
  if (d.length > 10 && d[0] === '1') d = d.slice(1);
  d = d.slice(0, 10);
  if (d.length === 0) return '';
  if (d.length <= 3) return `(${d}`;
  if (d.length <= 6) return `(${d.slice(0, 3)}) ${d.slice(3)}`;
  return `(${d.slice(0, 3)}) ${d.slice(3, 6)}-${d.slice(6)}`;
}

/**
 * The 10 national digits of a US number however it was stored ("3125551234", "+1-312-555-1234"),
 * or null when it is not one. Mirrors Core PhoneNumber.UsDigits.
 */
function usDigits(value: string | null | undefined): string | null {
  let d = (value ?? '').replace(/\D/g, '');
  if (d.length === 11 && d[0] === '1') d = d.slice(1);
  if (d.length !== 10 || /[01]/.test(d[0]) || /[01]/.test(d[3])) return null;
  return d;
}

/**
 * Read-only display of a stored phone, mirroring Core PhoneNumber.Display: a US number in any
 * spelling becomes (312) 555-1234; anything else is shown as stored; blank becomes `blank`.
 */
export function formatPhoneDisplay(value: string | null | undefined, blank = '—'): string {
  if (!value || !value.trim()) return blank;
  const d = usDigits(value);
  return d ? `(${d.slice(0, 3)}) ${d.slice(3, 6)}-${d.slice(6)}` : value.trim();
}

/** A stored phone pre-filled into an edit field: formatted when it is a US number, else as stored. */
export function phoneForEdit(value: string | null | undefined): string {
  return formatPhoneDisplay(value, '');
}

/**
 * Returns the error to show under the phone field, or null when it is acceptable.
 * Blank is reported as missing rather than malformed: "required" and "wrong shape" are
 * different mistakes and deserve different wording.
 */
export function validatePhone(value: string, label = 'Contact phone'): string | null {
  if (!value.trim()) return `${label} is required.`;
  return PHONE_PATTERN.test(value.trim()) ? null : PHONE_ERROR;
}

/**
 * Password rules mirrored from `PasswordPolicy` in IdVerify.Core.
 *
 * At least 8 characters, with an upper-case letter and a symbol. "Symbol" excludes whitespace:
 * with only two required classes a plain space would otherwise satisfy it on its own, so
 * "Password " would pass — and a trailing space is exactly what paste, autofill and some mail
 * clients silently strip, leaving a password that no longer works and no way to see why. Spaces
 * inside a password are fine; they just cannot be the symbol.
 *
 * The upper-case class is ASCII in both places, so 'Ü' alone does not satisfy it. Unicode
 * categories would need JavaScript's `u` flag, and the same literal string drives the C# check
 * and the HTML `pattern` attribute.
 *
 * If you change one, change the other. The C# side is the authority; this only saves a round trip.
 */
export const PASSWORD_PATTERN = /^(?=.*[A-Z])(?=.*[^A-Za-z0-9\s]).{8,128}$/;

/** The requirements stated positively — shown as guidance before anything has failed. */
export const PASSWORD_ERROR =
  'Password must be at least 8 characters and include an upper-case letter and a symbol ' +
  '(a space does not count).';

const MINIMUM_LENGTH = 8;
const MAXIMUM_LENGTH = 128;

/** Anything that is not an ASCII letter, digit or whitespace. Matches the C# IsSymbol. */
function isSymbol(ch: string): boolean {
  return !/[A-Za-z0-9]/.test(ch) && !/\s/.test(ch);
}

/**
 * Returns the message to show under the field, or null when the password is acceptable.
 *
 * Each requirement is checked separately so the message names the one that failed. Telling
 * someone who forgot a capital that their password "must be at least 8 characters and include an
 * upper-case letter and a symbol" makes them re-read all three rules to find the one they broke.
 *
 * Kept in step with `PasswordPolicy.Validate`, minus the two checks that need the server: reusing
 * the current password, and containing the account's own email address.
 */
export function validatePassword(value: string): string | null {
  if (!value) return 'A new password is required.';
  if (value.length > MAXIMUM_LENGTH) return `Password must be ${MAXIMUM_LENGTH} characters or fewer.`;
  if (value !== value.trim()) return 'Password must not start or end with a space.';
  if (value.length < MINIMUM_LENGTH) return `Password must be at least ${MINIMUM_LENGTH} characters.`;
  if (!/[A-Z]/.test(value)) return 'Password must include an upper-case letter.';
  if (![...value].some(isSymbol)) {
    return 'Password must include a symbol, such as ! ? # or @. A space does not count.';
  }
  return null;
}

// -- Website -------------------------------------------------------------------
// Mirrors IdVerify.Core.Helpers.WebsiteUrl - the same rule the server applies. A full address:
// http:// or https://, a real host name with a dot and a top-level domain, optional port and path.

export const WEBSITE_PATTERN =
  /^https?:\/\/([A-Za-z0-9]([A-Za-z0-9-]{0,61}[A-Za-z0-9])?\.)+[A-Za-z]{2,63}(:[0-9]{1,5})?([/?#][^\s]*)?$/i;

export const WEBSITE_ERROR =
  'Enter the website as a full address starting with http:// or https://, e.g. https://yourcompany.com.';

export function validateWebsite(value: string): string | null {
  const v = value.trim();
  if (!v) return 'Enter the company website.';
  if (v.length > 300) return 'The website address must be 300 characters or fewer.';
  return WEBSITE_PATTERN.test(v) ? null : WEBSITE_ERROR;
}

// -- Contact us form ------------------------------------------------------------
// Mirrors ContactRequestService (core/Services/ContactRequestService.cs) - change both together.
// The server is the authority; these only spare the visitor a round trip.
export const CONTACT_LIMITS = { name: 100, company: 150, email: 254, useCaseMin: 10, useCaseMax: 4000 } as const;

export interface ContactFields {
  name: string;
  companyName: string;
  email: string;
  useCase: string;
}

/** Field (Name, CompanyName, Email, UseCase) -> message, the same keys the server returns. */
export function validateContact(f: ContactFields): Record<string, string> {
  const errors: Record<string, string> = {};
  const name = f.name.trim();
  const company = f.companyName.trim();
  const email = f.email.trim();
  const useCase = f.useCase.trim();

  if (!name) errors.Name = 'Please enter your name.';
  else if (name.length > CONTACT_LIMITS.name) errors.Name = `Name can be at most ${CONTACT_LIMITS.name} characters.`;

  if (!company) errors.CompanyName = 'Please enter your company name.';
  else if (company.length > CONTACT_LIMITS.company)
    errors.CompanyName = `Company name can be at most ${CONTACT_LIMITS.company} characters.`;

  if (!email) errors.Email = 'Please enter your email address.';
  else if (email.length > CONTACT_LIMITS.email || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email))
    errors.Email = 'Please enter a valid email address.';

  if (useCase.length < CONTACT_LIMITS.useCaseMin)
    errors.UseCase = `Please describe your use case (at least ${CONTACT_LIMITS.useCaseMin} characters).`;
  else if (useCase.length > CONTACT_LIMITS.useCaseMax)
    errors.UseCase = `The description can be at most ${CONTACT_LIMITS.useCaseMax} characters.`;

  return errors;
}

// -- Support requests -------------------------------------------------------------

// Mirrors SupportTicketService (Core): MinSubject, MaxSubject, MinMessage, MaxMessage.
export const SUPPORT_LIMITS = { subjectMin: 3, subjectMax: 200, messageMin: 10, messageMax: 10000 } as const;

// Mirrors SupportAttachmentRules (Core): MaxFiles, MaxTotalBytes, AllowedExtensions. Images only.
// The server decodes and re-encodes every image; this only saves a round trip for obvious cases.
export const SUPPORT_ATTACHMENT_LIMITS = {
  maxFiles: 3,
  maxTotalBytes: 4 * 1024 * 1024,
  extensions: ['.png', '.jpg', '.jpeg', '.gif', '.webp'],
  accept: 'image/png,image/jpeg,image/gif,image/webp',
} as const;

/** The first problem with a set of images to attach, or null. */
export function validateSupportFiles(files: File[]): string | null {
  const { maxFiles, maxTotalBytes, extensions } = SUPPORT_ATTACHMENT_LIMITS;
  if (files.length > maxFiles) return `Attach at most ${maxFiles} images to one message.`;
  const bad = files.find((f) => !extensions.some((ext) => f.name.toLowerCase().endsWith(ext)));
  if (bad) return `${bad.name} is not an image. Only PNG, JPEG, GIF or WebP images can be attached.`;
  const total = files.reduce((sum, f) => sum + f.size, 0);
  if (total > maxTotalBytes) return `Images can be at most ${maxTotalBytes / (1024 * 1024)} MB in total per message.`;
  return null;
}

/** Values and labels as SupportTicketService.Label in Core. Sales is the contact form's. */
export const SUPPORT_CATEGORIES = [
  { value: 'Integration', label: 'Integration & API' },
  { value: 'VerificationResult', label: 'Verification result' },
  { value: 'Billing', label: 'Billing' },
  { value: 'AccountAccess', label: 'Account & access' },
  { value: 'Bug', label: 'Bug' },
  { value: 'Sales', label: 'Sales & pricing' },
  { value: 'Other', label: 'Other' },
] as const;

export const SUPPORT_PRIORITIES = ['Low', 'Normal', 'High', 'Urgent'] as const;

export const SUPPORT_STATUSES = [
  { value: 'Open', label: 'Open' },
  { value: 'InProgress', label: 'In Progress' },
  { value: 'WaitingOnClient', label: 'Waiting on Client' },
  { value: 'Resolved', label: 'Resolved' },
  { value: 'Closed', label: 'Closed' },
] as const;

export interface SupportTicketFields {
  subject: string;
  category: string;
  message: string;
}

/** Field name (Subject, Category, Message) -> message, the same keys the API returns. */
export function validateSupportTicket(fields: SupportTicketFields): Record<string, string> {
  const errors: Record<string, string> = {};
  const subject = fields.subject.trim();
  const message = fields.message.trim();

  if (subject.length < SUPPORT_LIMITS.subjectMin)
    errors.Subject = `Please enter a subject (at least ${SUPPORT_LIMITS.subjectMin} characters).`;
  else if (subject.length > SUPPORT_LIMITS.subjectMax)
    errors.Subject = `The subject can be at most ${SUPPORT_LIMITS.subjectMax} characters.`;

  if (!fields.category) errors.Category = 'Choose what the request is about.';

  const messageError = validateSupportMessage(message);
  if (messageError) errors.Message = messageError;

  return errors;
}

export function validateSupportMessage(message: string): string | null {
  const length = message.trim().length;
  if (length < SUPPORT_LIMITS.messageMin)
    return `Please write a little more (at least ${SUPPORT_LIMITS.messageMin} characters).`;
  if (length > SUPPORT_LIMITS.messageMax)
    return `A message can be at most ${SUPPORT_LIMITS.messageMax.toLocaleString('en-US')} characters.`;
  return null;
}
